/**
 * Named saves: what a slot is called, and how a list of them is presented.
 *
 * The store beneath this has always been able to hold any number of named
 * saves - `list`, `read`, `write`, `remove` against a string key - and the
 * game only ever used two of them, "world" and "world.backup". This is the
 * naming and the ordering that turns that capacity into something a player
 * can see and use.
 *
 * **Snapshots, not separate worlds.** A named save is a copy taken of the
 * world you are playing, and loading one copies it back. The game still plays
 * one live slot and still autosaves into it. That is a deliberate limit: the
 * alternative - the live game pointing at whichever slot you last picked -
 * means every autosave, every import and every "start again" has to know
 * which world it is talking about, and getting that wrong overwrites the
 * wrong save. A snapshot cannot do that.
 *
 * Pure: strings, numbers and sorting. No store, no DOM.
 */

import type { UiKey } from "../text/lang.js";

/** The live world, autosaved into. Unchanged, and not a named slot. */
export const LIVE_SLOT = "world";
/** Where the live world is copied before anything overwrites it. */
export const BACKUP_SLOT = "world.backup";

/**
 * What a player's own saves are keyed by.
 *
 * A prefix rather than a separate store, so one `list()` returns everything
 * and the two reserved slots are told apart by their key instead of by a
 * naming convention somebody has to remember.
 */
export const SLOT_PREFIX = "slot:";

/** Longest a name may be. Enough for "перед рейдом на дальній острів". */
export const MAX_SLOT_NAME = 40;

export function slotKey(name: string): string {
  return SLOT_PREFIX + name;
}

/** The player's name for a stored key, or null if it is not one of theirs. */
export function slotName(key: string): string | null {
  return key.startsWith(SLOT_PREFIX) ? key.slice(SLOT_PREFIX.length) : null;
}

/**
 * Why a name cannot be used, or null if it can.
 *
 * A key rather than a sentence: this is core, and core does not know which
 * language the player reads. Returning the Ukrainian text from here is how a
 * refusal would end up in Ukrainian in an English game.
 */
export interface NameProblem {
  readonly key: UiKey;
  /** The limit, for the message that mentions one. */
  readonly limit?: number;
}

export function slotNameProblem(
  name: string, taken: readonly string[] = [],
): NameProblem | null {
  const trimmed = name.trim();
  if (trimmed.length === 0) return { key: "slot-empty" };
  if (trimmed.length > MAX_SLOT_NAME) {
    return { key: "slot-long", limit: MAX_SLOT_NAME };
  }
  /*
   * Control characters only.
   *
   * Everything else is allowed on purpose: this is a name a person gives a
   * world in their own language, and a rule that permits only Latin letters
   * would refuse "перед рейдом" while accepting "asdf". The key goes into
   * IndexedDB, which takes any string.
   */
  if (/[\u0000-\u001f\u007f]/.test(trimmed)) {
    return { key: "slot-odd" };
  }
  if (taken.includes(trimmed)) return { key: "slot-taken" };
  return null;
}

export interface SlotView {
  /** What the player called it. */
  readonly name: string;
  /** The store key. */
  readonly key: string;
  readonly savedAt: number;
  readonly bytes: number;
}

/**
 * The player's saves, newest first.
 *
 * Newest first because the one you want is nearly always the one you just
 * made, and because a list that reorders itself as you save is worse than one
 * where the new row appears at the top.
 */
export function playerSlots(
  rows: readonly { name: string; savedAt: number; bytes: number }[],
): SlotView[] {
  const out: SlotView[] = [];
  for (const row of rows) {
    const name = slotName(row.name);
    if (name === null) continue;
    out.push({ name, key: row.name, savedAt: row.savedAt, bytes: row.bytes });
  }
  return out.sort((a, b) => b.savedAt - a.savedAt);
}

/**
 * The worlds the game keeps for you, one to a map.
 *
 * Every map autosaves into a live slot of its own - `world` for the кремль,
 * `world.adventure`, `world.hill` - and until now the save panel showed only
 * the first of them, as a single line of status called "поточний світ". The
 * others were on disk, unnamed, unlisted and unreachable: you could carry on
 * with whichever map you had open last, and the one before it existed but
 * had nowhere to be clicked. Reported as "there are no saves and there
 * should be three", which is exactly right - one named, and one for each map
 * that had been played.
 *
 * So they are rows now, like anybody's own saves, and the panel tells them
 * apart by where they came from rather than by hiding them.
 */
export interface WorldSlot {
  /** The stored key. */
  readonly key: string;
  /** Which map's world this is. */
  readonly mapId: string;
  readonly savedAt: number;
  readonly bytes: number;
}

export function worldSlots(
  rows: readonly { name: string; savedAt: number; bytes: number }[],
  maps: readonly { readonly id: string }[],
  defaultMapId: string,
): WorldSlot[] {
  const out: WorldSlot[] = [];
  for (const row of rows) {
    if (row.name === BACKUP_SLOT) continue;
    let mapId: string | null = null;
    if (row.name === LIVE_SLOT) mapId = defaultMapId;
    else if (row.name.startsWith(`${LIVE_SLOT}.`)) {
      const tail = row.name.slice(LIVE_SLOT.length + 1);
      if (maps.some((m) => m.id === tail)) mapId = tail;
    }
    if (mapId === null) continue;
    out.push({ key: row.name, mapId, savedAt: row.savedAt, bytes: row.bytes });
  }
  // Newest first, like everything else in this panel.
  return out.sort((a, b) => b.savedAt - a.savedAt);
}

/**
 * A name for a save nobody has bothered to name.
 *
 * Dated, so a row of them is still in an order that means something, and
 * numbered only when a date collides - which needs two saves in the same
 * minute and should not put "(2)" on every name to allow for it.
 */
export function suggestSlotName(
  taken: readonly string[], when: number = Date.now(),
): string {
  const d = new Date(when);
  const two = (n: number): string => String(n).padStart(2, "0");
  const base =
    `${two(d.getDate())}.${two(d.getMonth() + 1)} ` +
    `${two(d.getHours())}:${two(d.getMinutes())}`;
  if (!taken.includes(base)) return base;
  for (let n = 2; n < 100; n++) {
    const tried = `${base} (${n})`;
    if (!taken.includes(tried)) return tried;
  }
  return base;
}

/** "14.07.2025 21:30 · 128 КБ" - the line under a save's name. */
export function describeSlot(slot: { savedAt: number; bytes: number }): string {
  const d = new Date(slot.savedAt);
  const two = (n: number): string => String(n).padStart(2, "0");
  const when =
    `${two(d.getDate())}.${two(d.getMonth() + 1)}.${d.getFullYear()} ` +
    `${two(d.getHours())}:${two(d.getMinutes())}`;
  return `${when} · ${formatBytes(slot.bytes)}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}
